import { format } from 'date-fns';

/**
 * Parsing robusto di date-only (YYYY-MM-DD) in timezone locale.
 *
 * ⚠️ Motivo: `new Date('2026-01-28')` viene interpretata come UTC in JS e può
 * slittare di un giorno in timezone negative/positive quando poi si formatta in locale.
 * Questo rompe i confronti "previous period/year" (range sbagliati → compare non trova righe).
 */
const parseDateOnlyLocal = (dateStr: string): Date => {
  const raw = String(dateStr || '');
  const dateOnly = raw.split('T')[0];
  const match = dateOnly.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (match) {
    const [, y, m, d] = match;
    const year = parseInt(y, 10);
    const month = parseInt(m, 10);
    const day = parseInt(d, 10);
    if (!isNaN(year) && !isNaN(month) && !isNaN(day)) {
      return new Date(year, month - 1, day); // locale, non UTC
    }
  }

  // Fallback per stringhe ISO complete o altri formati.
  const dt = new Date(raw);
  // Se la stringa non è parseabile, restituiamo una Date "invalid" (NaN)
  // così che le funzioni a valle (es. `normalizeDateString`) possano
  // decidere in modo esplicito e non cadere su `new Date()` (oggi).
  return isNaN(dt.getTime()) ? new Date(NaN) : dt;
};

/**
 * Calcola il date range per "ultimi N giorni" (escludendo oggi).
 * Allineato a getThisMonthDateRange: i dati Shopify sono calcolati a ieri, quindi il range termina al giorno precedente.
 * @param daysCount - Numero di giorni da considerare (7, 30, 90)
 * @returns DateRange con from e to in formato YYYY-MM-DD (to = ieri)
 */
export const getLastNDaysDateRange = (
  daysCount: number,
): { from: string; to: string } => {
  const today = new Date();

  // Data di fine: ieri (i dati Shopify non includono oggi)
  const endDate = new Date(today);
  endDate.setDate(today.getDate() - 1);

  // Data di inizio: (N-1) giorni prima di endDate per avere N giorni totali
  // Esempio: Last 7 days = dal giorno (ieri - 6) a ieri = 7 giorni, oggi esclusa
  const startDate = new Date(endDate);
  startDate.setDate(endDate.getDate() - (daysCount - 1));

  return {
    from: format(startDate, 'yyyy-MM-dd'),
    to: format(endDate, 'yyyy-MM-dd'),
  };
};

/**
 * Ultimi N mesi di calendario (incluso il mese corrente, chiuso a ieri).
 * Evita mesi parziali all'inizio del range: "Last 6 months" parte dal 1° del mese,
 * non da un giorno rolling (es. 2 aprile) che sgancia Meta/Google dai totali mensili.
 * @param monthsCount - Numero di mesi di calendario (6, 12)
 * @returns DateRange con from (1° del mese) e to (ieri) in formato YYYY-MM-DD
 */
export const getLastNMonthsDateRange = (
  monthsCount: number,
): { from: string; to: string } => {
  const today = new Date();

  const endDate = new Date(today);
  endDate.setDate(today.getDate() - 1);

  // Dal 1° del mese che è (N-1) mesi prima del mese di endDate
  const startDate = new Date(
    endDate.getFullYear(),
    endDate.getMonth() - (monthsCount - 1),
    1,
  );

  return {
    from: format(startDate, 'yyyy-MM-dd'),
    to: format(endDate, 'yyyy-MM-dd'),
  };
};

/**
 * Calcola il date range per "ultimi N giorni" in modo uniforme (versione backend compatibile)
 * @param daysCount - Numero di giorni da considerare
 * @param currentDate - Data corrente in formato ISO string
 * @returns DateRange con from e to in formato YYYY-MM-DD
 */
export const calculateLastNDaysDateRange = (
  daysCount: number,
  currentDate: string,
): { from: string; to: string } => {
  const today = new Date(currentDate);

  // Data di fine: oggi
  const endDate = new Date(today);

  // Data di inizio: N giorni fa
  const startDate = new Date(today);
  startDate.setDate(today.getDate() - daysCount);

  return {
    from: format(startDate, 'yyyy-MM-dd'),
    to: format(endDate, 'yyyy-MM-dd'),
  };
};

/**
 * Formatta una data per la visualizzazione nell'header della tabella
 * @param dateStr - Data in formato ISO string o YYYY-MM-DD
 * @returns Data formattata come "MMM dd, yyyy"
 *
 * ⚠️ IMPORTANTE: Estrae sempre solo la parte data (YYYY-MM-DD) per evitare problemi di timezone.
 * Questo garantisce che le date mostrate nel frontend corrispondano esattamente a quelle nel backend.
 */
export const formatDateForDisplay = (dateStr: string): string => {
  try {
    // Estrai sempre solo la parte data (YYYY-MM-DD) dalla stringa ISO
    // Questo evita problemi di timezone quando si usa new Date()
    const dateOnly = dateStr.split('T')[0];
    const match = dateOnly.match(/^(\d{4})-(\d{2})-(\d{2})$/);

    if (match) {
      const [, yearStr, monthStr, dayStr] = match;
      const year = parseInt(yearStr, 10);
      const month = parseInt(monthStr, 10);
      const day = parseInt(dayStr, 10);

      if (
        !isNaN(year) &&
        !isNaN(month) &&
        !isNaN(day) &&
        month >= 1 &&
        month <= 12 &&
        day >= 1 &&
        day <= 31
      ) {
        // Crea una data locale (non UTC) per evitare problemi di timezone
        // Usa il mese 0-based come fa JavaScript
        const date = new Date(year, month - 1, day);
        return format(date, 'MMM dd, yyyy');
      }
    }

    // Fallback: se non riusciamo a estrarre la data, prova con new Date() come ultima risorsa
    const date = new Date(dateStr);
    if (!isNaN(date.getTime())) {
      return format(date, 'MMM dd, yyyy');
    }

    return 'Invalid date';
  } catch (error) {
    return 'Invalid date';
  }
};

/**
 * Normalizza una data string in formato YYYY-MM-DD
 * @param dateStr - Data in qualsiasi formato
 * @returns Data in formato YYYY-MM-DD
 */
export const normalizeDateString = (dateStr: string): string => {
  try {
    const date = parseDateOnlyLocal(dateStr);
    if (isNaN(date.getTime())) {
      return '';
    }
    return format(date, 'yyyy-MM-dd');
  } catch (error) {
    return '';
  }
};

/**
 * Calcola il periodo di comparazione precedente (stesso numero di giorni prima)
 * @param dateRange - Periodo corrente { from, to }
 * @returns Periodo precedente { from, to }
 */
export const getPreviousPeriodDateRange = (dateRange: {
  from: string;
  to: string;
}): { from: string; to: string } => {
  const fromDate = parseDateOnlyLocal(dateRange.from);
  const toDate = parseDateOnlyLocal(dateRange.to);

  // Calcola la durata del periodo in giorni
  const daysDiff = Math.ceil(
    (toDate.getTime() - fromDate.getTime()) / (1000 * 60 * 60 * 24),
  );

  // Calcola il periodo precedente (stesso numero di giorni prima)
  const prevToDate = new Date(fromDate);
  prevToDate.setDate(prevToDate.getDate() - 1); // Un giorno prima del from corrente

  const prevFromDate = new Date(prevToDate);
  prevFromDate.setDate(prevFromDate.getDate() - daysDiff);

  return {
    from: format(prevFromDate, 'yyyy-MM-dd'),
    to: format(prevToDate, 'yyyy-MM-dd'),
  };
};

/**
 * Calcola il periodo di comparazione dell'anno precedente (stesso periodo, anno scorso)
 * @param dateRange - Periodo corrente { from, to }
 * @returns Periodo anno precedente { from, to }
 */
export const getPreviousYearDateRange = (dateRange: {
  from: string;
  to: string;
}): { from: string; to: string } => {
  const fromDate = parseDateOnlyLocal(dateRange.from);
  const toDate = parseDateOnlyLocal(dateRange.to);

  // Sottrai un anno
  const prevYearFromDate = new Date(fromDate);
  prevYearFromDate.setFullYear(prevYearFromDate.getFullYear() - 1);

  const prevYearToDate = new Date(toDate);
  prevYearToDate.setFullYear(prevYearToDate.getFullYear() - 1);

  return {
    from: format(prevYearFromDate, 'yyyy-MM-dd'),
    to: format(prevYearToDate, 'yyyy-MM-dd'),
  };
};

/**
 * Calcola il date range per il mese corrente (dal primo giorno del mese a ieri)
 * I dati Shopify sono calcolati a ieri, quindi il range termina al giorno precedente
 * @returns DateRange con from (primo giorno del mese) e to (ieri) in formato YYYY-MM-DD
 */
export const getThisMonthDateRange = (): { from: string; to: string } => {
  const today = new Date();

  // Data di fine: ieri (i dati Shopify sono calcolati a ieri)
  const endDate = new Date(today);
  endDate.setDate(today.getDate() - 1);

  // Data di inizio: primo giorno del mese corrente
  const startDate = new Date(today.getFullYear(), today.getMonth(), 1);

  return {
    from: format(startDate, 'yyyy-MM-dd'),
    to: format(endDate, 'yyyy-MM-dd'),
  };
};

/**
 * Calcola il date range per l'anno corrente (dal 1° gennaio a ieri).
 * Con span > 120 giorni il backend Finance aggrega automaticamente per mese.
 */
export const getThisYearDateRange = (): { from: string; to: string } => {
  const today = new Date();

  const endDate = new Date(today);
  endDate.setDate(today.getDate() - 1);

  const startDate = new Date(today.getFullYear(), 0, 1);

  return {
    from: format(startDate, 'yyyy-MM-dd'),
    to: format(endDate, 'yyyy-MM-dd'),
  };
};
